import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { cn } from '@/lib/utils';
import { GripVertical, Pencil, Trash2 } from 'lucide-react';

interface SortableTagItemProps {
  id: string;
  name: string;
  color: string;
  ticketCount?: number;
  isActive: boolean;
  onClick: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

export const SortableTagItem = React.forwardRef<HTMLDivElement, SortableTagItemProps>(
  ({ id, name, color, ticketCount = 0, isActive, onClick, onEdit, onDelete }, forwardedRef) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
    };

    // Merge refs: useSortable's setNodeRef + forwarded ref
    const mergedRef = React.useCallback(
      (node: HTMLDivElement | null) => {
        setNodeRef(node);
        if (typeof forwardedRef === 'function') {
          forwardedRef(node);
        } else if (forwardedRef) {
          forwardedRef.current = node;
        }
      },
      [setNodeRef, forwardedRef]
    );

    return (
      <div
        ref={mergedRef}
        style={style}
        className={cn(
          "group flex items-center justify-between px-1 py-1.5 rounded-lg text-sm transition-colors",
          isDragging && "opacity-50 bg-muted shadow-lg z-50",
          isActive
            ? "bg-muted text-foreground font-medium"
            : "text-muted-foreground hover:text-foreground hover:bg-muted"
        )}
      >
        <div className="flex items-center gap-1 flex-1 min-w-0">
          {/* Drag handle */}
          <button
            {...attributes}
            {...listeners}
            className="p-1 cursor-grab hover:bg-muted rounded opacity-0 group-hover:opacity-100 transition-opacity touch-none"
            onClick={(e) => e.stopPropagation()}
          >
            <GripVertical className="h-3 w-3" />
          </button>

          {/* Tag content */}
          <div
            className="flex items-center gap-2 flex-1 min-w-0 px-1 py-0.5 cursor-pointer rounded hover:bg-muted/50"
            onClick={onClick}
          >
            <div
              className="w-2.5 h-2.5 rounded-sm flex-shrink-0"
              style={{ backgroundColor: color }}
            />
            <span className="truncate">{name}</span>
          </div>
        </div>

        <div className="flex items-center gap-1">
          <span className="text-xs opacity-60">{ticketCount}</span>
          <div className="hidden group-hover:flex items-center gap-0.5">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
              className="p-1 hover:bg-muted rounded"
            >
              <Pencil className="h-3 w-3" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              className="p-1 hover:bg-destructive/20 hover:text-destructive rounded"
            >
              <Trash2 className="h-3 w-3" />
            </button>
          </div>
        </div>
      </div>
    );
  }
);

SortableTagItem.displayName = 'SortableTagItem';
